<script setup lang="ts">
/*
* Компонент FileAttach - компонент для прикрепления и управления файлами
*
* Основные функции:
* - Выбор файлов через системный диалог с поддержкой множественного выбора
* - Отображение списка выбранных файлов с иконками и информацией о размере
* - Управление списком файлов (добавление, удаление отдельных файлов)
* - Отображение прогресса загрузки файлов на сервер
* - Поддержка ограничений по типам файлов через accept атрибут
*
* Особенности:
* - Интеграция с системой загрузки файлов через filesStore
* - Визуализация прогресса загрузки через VProgressLinear
* - Перетаскивание файлов на всю форму (область Ctrl+V): оверлей-подсказка на время перетаскивания, файлы попадают в штатный список — FileDropLayer + useFormFileDrop
* - Адаптивный дизайн для отображения списка файлов
*/

// Импорты хранилищ
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useFilesStore } from '@/modules/files/stores/filesStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import FileDropLayer from '@/modules/files/components/FileDropLayer.vue'
import { useFormFileDrop } from '@/modules/files/composables/useFormFileDrop'
import { isMobileDevice } from '@/modules/files/utils/isMobileDevice'
import { getCameraPermissionState } from '@/modules/files/utils/getCameraPermissionState'

// Импорты Vue

// Импорты изображений иконок файлов
import universal from '@images/icons/files/universal.svg'
import xsl from '@images/icons/files/xls.svg'
import pdf from '@images/icons/files/pdf.svg'
import doc from '@images/icons/files/doc.svg'
import jpg from '@images/icons/files/jpg.svg'

// Определение props компонента
const props = defineProps<{
  files?: File[] // Список выбранных файлов
  disabled?: boolean // Флаг блокировки взаимодействия с компонентом
  accept?: string // Строка accept для ограничения типов файлов
  /** Ctrl+V только при фокусе внутри контейнера (аудит с несколькими чек-листами) */
  pasteRequiresFocus?: boolean
}>()

// Определение emits компонента
const emit = defineEmits<{
  (e: 'update:files', v: File[]): void // Событие обновления списка файлов
}>()

let activeFileAttachId: string | null = null

// Инициализация хранилищ
const fileStore = useFilesStore()
const feedbackStore = useFeedbackStore()

// Реактивные состояния компонента
// Внутренний список файлов (синхронизируется с props.files)
const internal = ref<File[]>(props.files ?? [])

// Референс на скрытый input элемент для выбора файлов
const fileInputRef = ref<HTMLInputElement | null>(null)
const cameraInputRef = ref<HTMLInputElement | null>(null)
const isSourceDialogOpen = ref(false)

// Вычисляемые свойства
// Флаг активности загрузки файлов
const busy = computed<boolean>(() => fileStore.uploadState.active)

// Процент завершения текущей загрузки
const pct = computed<number>(() => fileStore.uploadState.pct)

/**
 * Публичный метод для открытия диалога выбора файлов
 *
 * Назначение:
 * - Программно активирует скрытый input элемент для выбора файлов
 * - Предоставляет внешний интерфейс для открытия диалога выбора
 * - Проверяет состояние disabled перед выполнением действия
 *
 * Использование:
 * - Может вызываться из родительского компонента через ref
 * - Используется для кастомных триггеров выбора файлов
 */
const openDialog = () => {
  if (props.disabled) return

  /* Safari рвёт user-gesture, если click() после await */
  void warnIfCameraDenied()

  /* На мобиле системный chooser часто без камеры — даём явный выбор */
  if (isMobileDevice()) {
    isSourceDialogOpen.value = true
    return
  }

  fileInputRef.value?.click()
}

const warnIfCameraDenied = async () => {
  const cameraPermission = await getCameraPermissionState()

  if (cameraPermission === 'denied') {
    feedbackStore.showToast({
      type: 'warning',
      message: 'Камера запрещена в настройках сайта. Съёмка фото недоступна — можно выбрать файл из галереи.',
    })
  }
}

const closeSourceDialog = () => {
  isSourceDialogOpen.value = false
}

const openCamera = () => {
  cameraInputRef.value?.click()
  closeSourceDialog()
}

const openFiles = () => {
  fileInputRef.value?.click()
  closeSourceDialog()
}

/**
 * Экспорт методов для использования извне
 *
 * Назначение:
 * - Предоставляет доступ к методам компонента через template refs
 * - Позволяет родительским компонентам управлять поведением FileAttach
 */
defineExpose({
  openDialog,
})

/**
 * Обработчик выбора файлов в системном диалоге
 *
 * Назначение:
 * - Обрабатывает событие change на input элементе
 * - Добавляет выбранные файлы во внутренний список
 * - Эмитит событие обновления для синхронизации с родительским компонентом
 * - Очищает значение input для возможности повторного выбора тех же файлов
 *
 * Алгоритм:
 * 1. Извлекает выбранные файлы из event.target
 * 2. Объединяет новые файлы с существующим списком
 * 3. Эмитит обновленный список файлов
 * 4. Сбрасывает значение input элемента
 *
 * Входные данные:
 * - e: Event - событие выбора файлов
 */
const onSelected = (e: Event) => {
  const input = e.target as HTMLInputElement
  if (!input.files?.length) return
  internal.value = [...internal.value, ...Array.from(input.files)]
  emit('update:files', internal.value)
  input.value = ''
}

/**
 * Удаление файла из списка по индексу
 *
 * Назначение:
 * - Удаляет конкретный файл из внутреннего списка
 * - Эмитит обновленный список файлов после удаления
 * - Проверяет состояние disabled перед выполнением удаления
 *
 * Входные данные:
 * - idx: number - индекс файла для удаления в массиве internal.value
 */
const removeAt = (idx: number) => {
  if (props.disabled) return
  internal.value.splice(idx, 1)
  emit('update:files', internal.value)
}

/**
 * Получение иконки файла на основе его имени
 *
 * Назначение:
 * - Определяет тип файла по расширению в имени файла
 * - Возвращает соответствующую SVG-иконку для визуального отображения
 * - Использует универсальную иконку для неизвестных типов файлов
 *
 * Поддерживаемые типы:
 * - Excel: xls, xlsx
 * - PDF: pdf
 * - Word: doc, docx
 * - Изображения: jpg, jpeg, png, gif
 * - Остальные: universal (универсальная иконка)
 *
 * Входные данные:
 * - fileName: string - имя файла для анализа расширения
 *
 * Возвращает:
 * - string: путь к SVG-иконке файла
 */
const getFileIcon = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase()
  switch (extension) {
    case 'xls': case 'xlsx': return xsl
    case 'pdf': return pdf
    case 'doc': case 'docx': return doc
    case 'jpg': case 'jpeg': case 'png': case 'gif': return jpg
    default: return universal
  }
}

/**
 * Форматирование размера файла для отображения
 *
 * Назначение:
 * - Преобразует размер файла в байтах в человекочитаемый формат
 * - Отображает размер в килобайтах или мегабайтах в зависимости от величины
 * - Обеспечивает округление до целых чисел для KB и одного знака для MB
 *
 * Входные данные:
 * - file: File - объект файла для получения размера
 *
 * Возвращает:
 * - string: отформатированный размер файла
 */
const getFileSize = (file: File): string => {
  const sizeInKb = file.size / 1024
  return sizeInKb < 1024
    ? `${Math.round(sizeInKb)} kb`
    : `${(sizeInKb / 1024).toFixed(1)} mb`
}

/**
 * Вотчер для синхронизации props.files с внутренним состоянием
 *
 * Назначение:
 * - Обеспечивает реактивную синхронизацию внешнего и внутреннего списков файлов
 * - Обрабатывает изменения props.files из родительского компонента
 * - immediate: true гарантирует первоначальную синхронизацию при монтировании
 */
watch(
  () => props.files,
  v => {
    internal.value = Array.isArray(v) ? v : []
  },
  { immediate: true },
)

const rootRef = ref<HTMLElement | null>(null)
const instanceId = `file-attach-${Math.random().toString(36).substring(2, 9)}`

const PASTE_SCOPE_SELECTOR = '.v-card, form, .v-navigation-drawer, .v-dialog, .audit-check-card'

const getPasteScope = (): HTMLElement | null => {
  return rootRef.value?.closest(PASTE_SCOPE_SELECTOR) ?? rootRef.value
}

// Перетаскивание файлов на всю форму — та же область, что и для Ctrl+V
const drop = useFormFileDrop({
  getScope: getPasteScope,
  isDisabled: () => !!props.disabled,
  addFiles: files => {
    internal.value = [...internal.value, ...files]
    emit('update:files', internal.value)
  },
})

const isFocusInsideScope = (): boolean => {
  const active = document.activeElement
  if (!(active instanceof Node)) return false

  const scope = getPasteScope()
  if (!scope) return false

  return scope.contains(active)
}

const makeActive = (e: Event) => {
  if (props.pasteRequiresFocus) return

  const target = e.target as HTMLElement
  if (!target) return

  const closestContainer = target.closest(PASTE_SCOPE_SELECTOR)
  if (!closestContainer) return

  const myContainer = rootRef.value?.closest(PASTE_SCOPE_SELECTOR)
  if (closestContainer === myContainer) {
    activeFileAttachId = instanceId
  }
}

const onPaste = (e: ClipboardEvent) => {
  if (props.disabled) return

  if (props.pasteRequiresFocus) {
    if (!isFocusInsideScope()) return
  }
  else if (activeFileAttachId !== instanceId) {
    return
  }

  const files = e.clipboardData?.files
  if (files && files.length > 0) {
    e.preventDefault()
    const fileArray = Array.from(files)
    internal.value = [...internal.value, ...fileArray]
    emit('update:files', internal.value)
  }
}

onMounted(() => {
  document.addEventListener('paste', onPaste)

  if (props.pasteRequiresFocus) return

  document.addEventListener('click', makeActive)
  document.addEventListener('focusin', makeActive)

  const myContainer = rootRef.value?.closest(PASTE_SCOPE_SELECTOR)
  if (myContainer) {
    const isInDialog = myContainer.classList.contains('v-dialog')
                       || myContainer.classList.contains('v-navigation-drawer')
                       || myContainer.closest('.v-dialog, .v-navigation-drawer')

    if (isInDialog || !activeFileAttachId) {
      activeFileAttachId = instanceId
    }
  }
  else if (!activeFileAttachId) {
    activeFileAttachId = instanceId
  }
})

onUnmounted(() => {
  document.removeEventListener('paste', onPaste)
  document.removeEventListener('click', makeActive)
  document.removeEventListener('focusin', makeActive)

  if (activeFileAttachId === instanceId) {
    activeFileAttachId = null
  }
})
</script>

<template>
  <div ref="rootRef" class="file-attach">
    <input
      ref="fileInputRef"
      type="file"
      class="file-attach__native-input"
      multiple
      :accept="accept"
      @change="onSelected"
    >

    <input
      ref="cameraInputRef"
      type="file"
      class="file-attach__native-input"
      accept="image/*"
      capture="environment"
      @change="onSelected"
    >

    <VDialog
      v-model="isSourceDialogOpen"
      max-width="400"
      :fullscreen="false"
    >
      <VCard>
        <div class="d-flex flex-column ga-4 pa-4">
          <div class="d-flex align-center justify-space-between">
            <span class="text-h4">Выберите действие</span>
            <CustomCloseButton :close-action="closeSourceDialog" />
          </div>

          <VBtn
            variant="outlined"
            class="w-100"
            size="large"
            prepend-icon="ri-camera-line"
            @click="openCamera"
          >
            Камера
          </VBtn>

          <VBtn
            variant="outlined"
            class="w-100"
            size="large"
            prepend-icon="ri-folder-line"
            @click="openFiles"
          >
            Файлы
          </VBtn>
        </div>
      </VCard>
    </VDialog>

    <FileDropLayer :rect="drop.rect.value" />

    <!-- Список выбранных файлов в новом дизайне -->
    <div v-if="internal.length" class="file-attach__items d-flex flex-wrap gap-2">
      <div
        v-for="(f, i) in internal"
        :key="`${f.name}-${i}`"
        class="file-attach__row"
      >
        <!-- Иконка файла -->
        <VIcon :icon="getFileIcon(f.name)" size="18" class="file-attach__icon" />

        <!-- Контент: имя + размер -->
        <div class="file-attach__content d-flex flex-column text-left">
          <VTooltip :text="f.name">
            <template #activator="{ props }">
              <span v-bind="props" class="file-attach__name text-truncate">{{ f.name }}</span>
            </template>
          </VTooltip>
          <span class="file-attach__size text-caption text-disabled">{{ getFileSize(f) }}</span>
        </div>

        <!-- Кнопка удаления -->
        <VIconBtn
          class="file-attach__delete-btn rounded-sm"
          color="primary"
          icon="ri-close-line"
          variant="plain"
          :size="52"
          :disabled="disabled"
          aria-label="Удалить файл"
          title="Удалить"
          @click="removeAt(i)"
        />
      </div>
    </div>

    <!-- Прогресс загрузки -->
    <div v-if="busy" class="w-100 mt-2">
      <VProgressLinear
        :model-value="pct"
        color="primary"
        height="8"
        rounded
      >
        <template #default="{ value }">
          <strong>{{ Math.ceil(value) }}%</strong>
        </template>
      </VProgressLinear>
    </div>
  </div>
</template>

<style scoped lang="scss">
.file-attach {
  position: relative;
}

/* display:none на iOS Safari блокирует programmatic click() */
.file-attach__native-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  overflow: hidden;
}

.file-attach__row {
  max-width: 17.5rem;
  width: 100%;
  min-height: 2.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 6px;
  background: rgb(var(--v-theme-primary), var(--v-selected-opacity));
}

.file-attach__content,
.file-attach__icon {
  padding: 8px 0;
}

.file-attach__delete-btn {
  margin-top: 0;
  margin-bottom: 0;
  align-self: stretch;
}

.file-attach__icon {
  flex: 0 0 auto;
  margin-left: 0.75rem;
}

.file-attach__content {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  padding-right: 0.25rem;
}

.file-attach__name {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}
</style>
